import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 60_000;

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialHash = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const { buildHash } = (await res.json()) as { buildHash: string };

      if (initialHash.current === null) {
        initialHash.current = buildHash;
      } else if (buildHash !== initialHash.current) {
        setUpdateAvailable(true);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    } catch {
      // Network error — ignore and retry next interval
    }
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    checkVersion();
    timerRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkVersion]);

  return updateAvailable;
}
